import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";

type AuthSession = {
  user: {
    id: string;
    email: string;
    emailVerified: boolean;
    name: string;
    image?: string | null;
    createdAt?: Date | string;
    updatedAt?: Date | string;
  };
  session: {
    id: string;
    userId: string;
    token: string;
    expiresAt: Date | string;
    ipAddress?: string | null;
    userAgent?: string | null;
    createdAt?: Date | string;
    updatedAt?: Date | string;
  };
};

export function useAuth() {
  const navigate = useNavigate();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await authClient.getSession();
      setSession((data as AuthSession | null) ?? null);
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const logout = useCallback(async () => {
    await authClient.signOut();
    setSession(null);
    navigate({ to: "/login" });
  }, [navigate]);

  return { session, isLoading, logout, refetch: fetchSession };
}
